import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiSearch } from "react-icons/fi";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { GetStockReportApi } from "../../../../Api/Store/Stock";
import { getallCategory } from "../../../../Redux/actions/Operationsactions/getcategory";
import { getallProject } from "../../../../Redux/actions/getprojects";
import { filterStoreProjects } from "../../projectFilters";
import DateRangeFilter from "../../../../Common/DateFilter";
import StatusChip from "../../../Common/StatusChip";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";

// Helper for formatting numeric values with thousand commas
const formatNum = (val) => {
  const num = Number(val ?? 0);
  if (isNaN(num)) {
    return "0";
  }
  return num.toLocaleString(undefined, {
    minimumFractionDigits: num % 1 !== 0 ? 1 : 0,
    maximumFractionDigits: 2,
  });
};

const StockReport = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);

  const rolesData = useSelector((state) => state.GetAllRole);
  const [reportRows, setReportRows] = useState([]);
  const categoryData = useSelector((state) => state.getAllCategory);
  const projectData = useSelector((state) => state.getProject);

  // Column show/hide state matching other pages
  const [columnVisibility, setColumnVisibility] = useState({});

  // Filter valid store projects
  const availableProjects = useMemo(() => {
    const rawList = rolesData?.data?.Projects || projectData?.data || [];
    return filterStoreProjects(rawList);
  }, [rolesData?.data?.Projects, projectData?.data]);

  // Filter states
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDates, setFilterDates] = useState(null);

  // Initial load
  useEffect(() => {
    dispatch(getallCategory());
    dispatch(getallProject());
  }, [dispatch]);

  // Reload the movement report whenever the project or date range changes
  useEffect(() => {
    let cancelled = false;
    GetStockReportApi(selectedProjectId, filterDates?.from, filterDates?.to)
      .then((res) => {
        if (cancelled) {
          return;
        }
        if (res.data?.IsSuccess) {
          setReportRows(res.data.Data || []);
        } else {
          setReportRows([]);
          toast.error(res.data?.Message || "Failed to fetch stock report");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReportRows([]);
          toast.error("Something went wrong while fetching stock report!");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, filterDates]);

  // Rows come from the backend; status is derived from the closing balance and alert level
  const allStockRows = useMemo(
    () =>
      reportRows.map((r) => {
        const currentBal = Number(r.ClosingBalance || 0);
        const alertLvl = Number(r.RestockLevel || 0);
        let status = "Healthy";
        if (currentBal <= 0) {
          status = "Out of Stock";
        } else if (currentBal <= alertLvl) {
          status = "Low Stock";
        }
        const adj = Number(r.Adjustments || 0);
        return {
          id: r.MaterialId,
          sku: r.ItemCode || "",
          name: r.ItemName,
          category: r.CategoryName || "",
          uom: r.UomName,
          openingStock: r.OpeningStock,
          totalInward: r.TotalInward,
          totalOutward: r.TotalOutward,
          transferredStr: `+${r.TransferredIn} / -${r.TransferredOut}`,
          adjustments: adj > 0 ? `+${adj}` : `${adj}`,
          currentBalance: currentBal,
          alertLevel: alertLvl,
          status,
        };
      }),
    [reportRows],
  );

  // Filtered rows (live auto-applied)
  const filteredRows = useMemo(() => {
    return allStockRows.filter((row) => {
      if (selectedCategory && row.category.toLowerCase() !== selectedCategory.toLowerCase()) {
        return false;
      }
      if (searchTerm) {
        const query = searchTerm.toLowerCase();
        const matchesName = (row.name || "").toLowerCase().includes(query);
        const matchesSku = (row.sku || "").toLowerCase().includes(query);
        const matchesCat = (row.category || "").toLowerCase().includes(query);
        if (!matchesName && !matchesSku && !matchesCat) {
          return false;
        }
      }
      return true;
    });
  }, [allStockRows, selectedCategory, searchTerm]);

  // Column Definitions for AG Grid (standard list table layout matching other pages)
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Item SKU",
        field: "sku",
        minWidth: 140,
        flex: 1,
        cellRenderer: (params) => (
          <span style={{ fontWeight: 600, color: "#056363" }}>{params.value}</span>
        ),
      },
      {
        headerName: "Material Name",
        field: "name",
        minWidth: 190,
        flex: 1.5,
      },
      {
        headerName: "Category",
        field: "category",
        minWidth: 130,
        flex: 1,
      },
      {
        headerName: "UOM",
        field: "uom",
        minWidth: 90,
        width: 100,
      },
      {
        headerName: "Opening Stock",
        field: "openingStock",
        minWidth: 120,
        flex: 1,
        valueFormatter: (p) => formatNum(p.value),
      },
      {
        headerName: "Total Inward (GRN)",
        field: "totalInward",
        minWidth: 150,
        flex: 1,
        valueFormatter: (p) => formatNum(p.value),
        cellStyle: { color: "#10b981", fontWeight: 600 },
      },
      {
        headerName: "Total Outward (Issued)",
        field: "totalOutward",
        minWidth: 160,
        flex: 1,
        valueFormatter: (p) => formatNum(p.value),
        cellStyle: { color: "#64748b", fontWeight: 600 },
      },
      {
        headerName: "Transferred In/Out",
        field: "transferredStr",
        minWidth: 150,
        flex: 1,
      },
      {
        headerName: "Adjustments",
        field: "adjustments",
        minWidth: 120,
        flex: 1,
        cellStyle: (p) => {
          const val = Number(p.value ?? 0);
          return {
            color: val < 0 ? "#ef4444" : val > 0 ? "#10b981" : "#64748b",
            fontWeight: 600,
          };
        },
      },
      {
        headerName: "Current Balance",
        field: "currentBalance",
        minWidth: 130,
        flex: 1,
        valueFormatter: (p) => formatNum(p.value),
        cellStyle: { fontWeight: 700, color: "#0f172a" },
      },
      {
        headerName: "Alert Level",
        field: "alertLevel",
        minWidth: 110,
        flex: 1,
        valueFormatter: (p) => formatNum(p.value),
      },
      {
        headerName: "Status",
        field: "status",
        minWidth: 130,
        width: 130,
        cellRenderer: (params) => {
          if (!params.data) {
            return null;
          }
          const status = params.value || "Healthy";
          let type = "success";
          if (status === "Low Stock") {
            type = "warning";
          } else if (status === "Out of Stock") {
            type = "error";
          }
          return <StatusChip status={status} type={type} />;
        },
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (p) => p.value,
      valueFormatter: (p) =>
        p.value === null || p.value === undefined || p.value === "" ? "N/A" : p.value,
      minWidth: 100,
    }),
    [],
  );

  // Column visibility handling matching other pages (DemandNoteTable, StocksTable)
  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  return (
    <>
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Stock Report"
        >
          Stock Report
        </div>
      </div>

      {/* Top Filters Toolbar matching theme */}
      <div className="stock-filters-card mt-2 mb-1">
        <div className="stock-filters-grid">
          {/* Project / Store */}
          <div className="filter-group">
            <label className="filter-label">Project / Store *</label>
            <select
              className="filter-select"
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
            >
              <option value="">All Projects</option>
              {availableProjects.map((p) => (
                <option key={p.ProjectId} value={p.ProjectId}>
                  {p.ProjectName}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div className="filter-group">
            <label className="filter-label">Date Range</label>
            <DateRangeFilter
              initialFrom={filterDates?.from || ""}
              initialTo={filterDates?.to || ""}
              onChange={(val) => {
                setFilterDates(val || null);
              }}
            />
          </div>

          {/* Category */}
          <div className="filter-group">
            <label className="filter-label">Category</label>
            <select
              className="filter-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {(categoryData?.data || []).map((cat) => (
                <option key={cat.Id || cat.CategoryName} value={cat.CategoryName}>
                  {cat.CategoryName}
                </option>
              ))}
            </select>
          </div>

          {/* Search Material */}
          <div className="filter-group">
            <label className="filter-label">Search Material</label>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <FiSearch
                style={{
                  position: "absolute",
                  left: "10px",
                  color: "#94a3b8",
                  fontSize: "14px",
                }}
              />
              <input
                type="text"
                className="filter-input"
                style={{ paddingLeft: "32px" }}
                placeholder="Search by name or SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Column Visibility */}
          <div className="d-flex align-items-end pb-1" style={{ flexShrink: 0 }}>
            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* Standard AG Grid Table (Matches other pages) */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div className="ag-theme-alpine vouchers_table_height3 mt-1" style={{ width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredRows || []}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              quickFilterText={searchTerm}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default StockReport;
